import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { countGeometryStats, damp, dampVec3 } from './math';
import type {
  CityDefinition,
  CityInstance,
  InteractiveElement,
  MotifId,
  NarrativePOI,
  SpecimenInstance,
} from './types';

export interface EngineTelemetry {
  fps: number;
  mode: 'city' | 'specimen';
  activePoiId: MotifId;
  activeSpecimenId: string | null;
  temporal: number;
  drawCalls: number;
  triangles: number;
  vertices: number;
  meshes: number;
  bloomEnabled: boolean;
  wireframe: boolean;
  autoOrbit: boolean;
}

export interface EngineCallbacks {
  onHoverInteractive?: (item: InteractiveElement | null, screenPos: { x: number; y: number }) => void;
  onActivateInteractive?: (item: InteractiveElement, point: THREE.Vector3) => void;
  onPoiChanged?: (poi: NarrativePOI) => void;
  onTelemetryUpdate?: (telemetry: EngineTelemetry) => void;
}

const AtmosphericGradeShader = {
  uniforms: {
    tDiffuse: { value: null },
    uTime: { value: 0 },
    uMemory: { value: 0.15 },
    uTelescopeFocus: { value: 0 },
    uAspect: { value: 1.77 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uMemory;
    uniform float uTelescopeFocus;
    uniform float uAspect;
    varying vec2 vUv;

    float rand(vec2 co) {
      return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
    }

    void main() {
      vec2 centered = (vUv - 0.5) * vec2(uAspect, 1.0);
      float r = length(centered);

      float chroma = (0.0006 + 0.0032 * uTelescopeFocus) * r;
      vec2 dir = normalize(vUv - 0.5 + 1e-5);
      float cr = texture2D(tDiffuse, vUv + dir * chroma).r;
      float cg = texture2D(tDiffuse, vUv).g;
      float cb = texture2D(tDiffuse, vUv - dir * chroma).b;
      vec3 col = vec3(cr, cg, cb);

      vec3 desireLift = vec3(1.03, 0.99, 0.96);
      vec3 memoryLift = vec3(0.93, 0.98, 1.06);
      col *= mix(desireLift, memoryLift, uMemory);

      if (uTelescopeFocus > 0.01) {
        float ring1 = abs(r - 0.36);
        float ring2 = abs(r - 0.24);
        float crosshair = min(abs(centered.x), abs(centered.y));
        float reticle = smoothstep(0.0022, 0.0, ring1) * 0.45
                      + smoothstep(0.0018, 0.0, ring2) * 0.3
                      + smoothstep(0.0012, 0.0, crosshair) * step(0.04, r) * step(r, 0.38) * 0.25;
        vec3 brassTint = mix(vec3(0.96, 0.78, 0.42), vec3(0.62, 0.86, 1.0), uMemory);
        col += brassTint * reticle * uTelescopeFocus;
        float lensVignette = smoothstep(0.58, 0.35, r);
        col *= mix(1.0, 0.35 + 0.65 * lensVignette, uTelescopeFocus * 0.75);
      }

      float vig = smoothstep(1.22, 0.28, r);
      col *= (0.62 + 0.38 * vig);

      float grain = (rand(vUv + fract(uTime * 0.13)) - 0.5) * 0.015;
      col += grain;

      gl_FragColor = vec4(max(col, 0.0), 1.0);
    }
  `,
};

export class InvisibleCitiesEngine {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly controls: OrbitControls;
  readonly composer: EffectComposer;
  readonly bloomPass: UnrealBloomPass;
  readonly gradePass: ShaderPass;

  private container: HTMLElement;
  private callbacks: EngineCallbacks;
  private cityDef: CityDefinition;
  private cityInstance!: CityInstance;

  private specimenGroup = new THREE.Group();
  private activeSpecimen: SpecimenInstance | null = null;
  private activeSpecimenId: string | null = null;
  private specimenPedestal!: THREE.Group;

  private mode: 'city' | 'specimen' = 'city';
  private activePoi: NarrativePOI;
  private targetCamPos = new THREE.Vector3();
  private targetLookAt = new THREE.Vector3();
  private currentFov = 44;
  private targetFov = 44;
  private cameraFlightWeight = 0;

  private temporalTarget = 0.15;
  private temporalCurrent = 0.15;
  private wireframeEnabled = false;
  private bloomEnabled = true;
  private autoOrbit = true;
  private userDragging = false;

  private raycaster = new THREE.Raycaster();
  private pointerNDC = new THREE.Vector2(-99, -99);
  private pointerScreen = { x: 0, y: 0 };
  private hoveredItem: InteractiveElement | null = null;
  private pointerDownPos = { x: 0, y: 0 };

  private clock = new THREE.Clock();
  private fpsFrames = 0;
  private fpsAccum = 0;
  private currentFps = 60;
  private geomStats = { vertices: 0, triangles: 0, meshes: 0 };
  private animFrameId = 0;
  private fogExp2: THREE.FogExp2;

  constructor(
    container: HTMLElement,
    cityDef: CityDefinition,
    callbacks: EngineCallbacks = {}
  ) {
    this.container = container;
    this.cityDef = cityDef;
    this.callbacks = callbacks;
    this.activePoi = cityDef.pois[0];

    this.scene = new THREE.Scene();
    this.fogExp2 = new THREE.FogExp2(0x160f1d, 0.0075);
    this.scene.fog = this.fogExp2;

    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || window.innerHeight;

    this.camera = new THREE.PerspectiveCamera(44, width / height, 0.25, 360);
    this.camera.position.set(...this.activePoi.cameraPos);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: true,
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(this.renderer.domElement);

    // Procedural PMREM studio environment so metallic brass, bronze & nacre gleam
    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    const roomEnv = new RoomEnvironment();
    this.scene.environment = pmremGenerator.fromScene(roomEnv, 0.04).texture;
    this.scene.environmentIntensity = 0.55;
    roomEnv.dispose();
    pmremGenerator.dispose();

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.055;
    this.controls.maxDistance = 115;
    this.controls.minDistance = 2.0;
    this.controls.maxPolarAngle = Math.PI * 0.495;
    this.controls.target.set(...this.activePoi.cameraTarget);

    this.targetCamPos.copy(this.camera.position);
    this.targetLookAt.copy(this.controls.target);

    this.composer = new EffectComposer(this.renderer);
    const renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(renderPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      0.38, // restrained bloom strength
      0.48, // radius
      0.82  // high threshold so only true emissives bloom
    );
    this.composer.addPass(this.bloomPass);

    this.gradePass = new ShaderPass(AtmosphericGradeShader);
    this.gradePass.uniforms.uAspect.value = width / Math.max(height, 1);
    this.composer.addPass(this.gradePass);

    this.cityInstance = this.cityDef.build(this.scene);

    this.specimenGroup.visible = false;
    this.specimenPedestal = this.createSpecimenStage();
    this.specimenGroup.add(this.specimenPedestal);
    this.scene.add(this.specimenGroup);

    this.recomputeGeometryStats();
    this.bindEvents();
  }

  private createSpecimenStage(): THREE.Group {
    const stage = new THREE.Group();
    const baseGeo = new THREE.CylinderGeometry(4.6, 5.1, 0.28, 64);
    const baseMat = new THREE.MeshStandardMaterial({
      color: 0x20192b,
      roughness: 0.35,
      metalness: 0.65,
    });
    const baseMesh = new THREE.Mesh(baseGeo, baseMat);
    baseMesh.position.y = -0.15;
    baseMesh.receiveShadow = true;
    stage.add(baseMesh);

    for (const radius of [2.2, 3.4, 4.4]) {
      const ringGeo = new THREE.RingGeometry(radius - 0.025, radius + 0.025, 96);
      ringGeo.rotateX(-Math.PI / 2);
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0xd4a55a,
        transparent: true,
        opacity: 0.42,
        side: THREE.DoubleSide,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.y = 0.01;
      stage.add(ring);
    }

    const specHemi = new THREE.HemisphereLight(0xffe0b5, 0x24304c, 1.35);
    stage.add(specHemi);

    const specDir = new THREE.DirectionalLight(0xffdfa6, 2.2);
    specDir.position.set(7, 12, 9);
    stage.add(specDir);

    const rimLight = new THREE.PointLight(0x63a4ff, 18, 28, 1.2);
    rimLight.position.set(-7, 6, -6);
    stage.add(rimLight);

    return stage;
  }

  private recomputeGeometryStats(): void {
    const targetRoot =
      this.mode === 'city' ? this.cityInstance.root : this.specimenGroup;
    this.geomStats = countGeometryStats(targetRoot);
  }

  private bindEvents(): void {
    window.addEventListener('resize', this.handleResize);

    const dom = this.renderer.domElement;
    dom.addEventListener('pointermove', this.handlePointerMove);
    dom.addEventListener('pointerdown', this.handlePointerDown);
    dom.addEventListener('pointerup', this.handlePointerUp);

    this.controls.addEventListener('start', () => {
      this.userDragging = true;
      this.cameraFlightWeight = 0;
    });
    this.controls.addEventListener('end', () => {
      this.userDragging = false;
    });
  }

  private handleResize = (): void => {
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;
    this.camera.aspect = width / Math.max(height, 1);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.composer.setSize(width, height);
    this.gradePass.uniforms.uAspect.value = width / Math.max(height, 1);
  };

  private handlePointerMove = (e: PointerEvent): void => {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointerScreen.x = e.clientX;
    this.pointerScreen.y = e.clientY;
    this.pointerNDC.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointerNDC.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  };

  private handlePointerDown = (e: PointerEvent): void => {
    this.pointerDownPos.x = e.clientX;
    this.pointerDownPos.y = e.clientY;
  };

  private handlePointerUp = (e: PointerEvent): void => {
    const dx = e.clientX - this.pointerDownPos.x;
    const dy = e.clientY - this.pointerDownPos.y;
    if (Math.hypot(dx, dy) > 6) return;

    if (this.hoveredItem && this.mode === 'city') {
      const hitPoint = new THREE.Vector3();
      this.hoveredItem.object.getWorldPosition(hitPoint);
      this.cityInstance.triggerMotifRipple(this.hoveredItem.motif, hitPoint);
      this.hoveredItem.onActivate?.(hitPoint);
      this.callbacks.onActivateInteractive?.(this.hoveredItem, hitPoint);
    }
  };

  private updateRaycaster(): void {
    if (this.mode !== 'city' || this.userDragging || this.pointerNDC.x < -1) {
      if (this.hoveredItem) {
        this.hoveredItem.onHover?.(false);
        this.hoveredItem = null;
        this.renderer.domElement.style.cursor = 'grab';
        this.callbacks.onHoverInteractive?.(null, this.pointerScreen);
      }
      return;
    }

    this.raycaster.setFromCamera(this.pointerNDC, this.camera);
    const roots = this.cityInstance.interactables.map((item) => item.object);
    const hits = this.raycaster.intersectObjects(roots, true);

    let matched: InteractiveElement | null = null;
    if (hits.length > 0) {
      const hitObj = hits[0].object;
      for (const item of this.cityInstance.interactables) {
        let cur: THREE.Object3D | null = hitObj;
        while (cur) {
          if (cur === item.object) {
            matched = item;
            break;
          }
          cur = cur.parent;
        }
        if (matched) break;
      }
    }

    if (matched !== this.hoveredItem) {
      this.hoveredItem?.onHover?.(false);
      this.hoveredItem = matched;
      this.hoveredItem?.onHover?.(true);
      this.renderer.domElement.style.cursor = matched ? 'pointer' : 'grab';
    }

    this.callbacks.onHoverInteractive?.(this.hoveredItem, this.pointerScreen);
  }

  public selectPOI(poiId: MotifId, syncTemporal = true): void {
    const poi = this.cityDef.pois.find((p) => p.id === poiId);
    if (!poi) return;

    if (this.mode !== 'city') {
      this.setMode('city');
    }

    this.activePoi = poi;
    this.targetCamPos.set(...poi.cameraPos);
    this.targetLookAt.set(...poi.cameraTarget);
    this.targetFov = poi.fov ?? 44;
    this.cameraFlightWeight = 1.0;

    if (syncTemporal && typeof poi.suggestedTemporal === 'number') {
      this.temporalTarget = poi.suggestedTemporal;
    }

    this.cityInstance.temporalUniforms.uTelescopeFocus.value =
      poi.id === 'telescopes' ? 1.0 : 0.0;

    this.cityInstance.triggerMotifRipple(
      poi.id,
      new THREE.Vector3(...poi.cameraTarget)
    );
    this.callbacks.onPoiChanged?.(poi);
  }

  public setTemporalState(value: number): void {
    this.temporalTarget = THREE.MathUtils.clamp(value, 0, 1);
  }

  public getTemporalState(): number {
    return this.temporalTarget;
  }

  public setMode(mode: 'city' | 'specimen', specimenId?: string): void {
    this.mode = mode;

    if (mode === 'city') {
      this.cityInstance.root.visible = true;
      this.specimenGroup.visible = false;
      this.activeSpecimenId = null;
      this.selectPOI(this.activePoi.id, false);
      this.recomputeGeometryStats();
      return;
    }

    const targetSpecId = specimenId ?? this.cityDef.specimens[0]?.id;
    const specDef =
      this.cityDef.specimens.find((s) => s.id === targetSpecId) ??
      this.cityDef.specimens[0];
    if (!specDef) return;

    this.cityInstance.root.visible = false;
    this.specimenGroup.visible = true;

    if (this.activeSpecimen) {
      this.specimenGroup.remove(this.activeSpecimen.group);
      this.activeSpecimen.dispose();
      this.activeSpecimen = null;
    }

    this.activeSpecimenId = specDef.id;
    this.activeSpecimen = specDef.build(this.cityInstance.temporalUniforms);
    this.specimenGroup.add(this.activeSpecimen.group);

    if (this.wireframeEnabled) {
      this.applyWireframeToGroup(this.activeSpecimen.group, true);
    }

    this.targetCamPos.set(...specDef.cameraPos);
    this.targetLookAt.set(...specDef.cameraTarget);
    this.targetFov = 38;
    this.cameraFlightWeight = 1.0;
    this.cityInstance.temporalUniforms.uTelescopeFocus.value = 0;

    this.recomputeGeometryStats();
  }

  public setWireframe(enabled: boolean): void {
    this.wireframeEnabled = enabled;
    this.cityInstance.setWireframe(enabled);
    if (this.activeSpecimen) {
      this.applyWireframeToGroup(this.activeSpecimen.group, enabled);
    }
  }

  private applyWireframeToGroup(group: THREE.Object3D, enabled: boolean): void {
    group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        const mat = obj.material;
        if (Array.isArray(mat)) {
          mat.forEach((m) => {
            if ('wireframe' in m) m.wireframe = enabled;
          });
        } else if (mat && 'wireframe' in mat) {
          mat.wireframe = enabled;
        }
      }
    });
  }

  public setBloomEnabled(enabled: boolean): void {
    this.bloomEnabled = enabled;
    this.bloomPass.enabled = enabled;
  }

  public setAutoOrbit(enabled: boolean): void {
    this.autoOrbit = enabled;
  }

  public triggerMotif(motif: MotifId): void {
    this.cityInstance.triggerMotifRipple(motif);
  }

  public getActiveCity(): CityDefinition {
    return this.cityDef;
  }

  public getActivePOI(): NarrativePOI {
    return this.activePoi;
  }

  public getActiveSpecimenMeta() {
    if (this.mode !== 'specimen' || !this.activeSpecimenId) return null;
    const def = this.cityDef.specimens.find((s) => s.id === this.activeSpecimenId);
    return {
      definition: def,
      meta: this.activeSpecimen?.meta ?? null,
    };
  }

  public getCityTelemetry() {
    return this.cityInstance.getTelemetry();
  }

  public getTelemetry(): EngineTelemetry {
    return {
      fps: this.currentFps,
      mode: this.mode,
      activePoiId: this.activePoi.id,
      activeSpecimenId: this.activeSpecimenId,
      temporal: Number(this.temporalCurrent.toFixed(2)),
      drawCalls: this.renderer.info.render.calls,
      triangles: this.geomStats.triangles,
      vertices: this.geomStats.vertices,
      meshes: this.geomStats.meshes,
      bloomEnabled: this.bloomEnabled,
      wireframe: this.wireframeEnabled,
      autoOrbit: this.autoOrbit,
    };
  }

  public start(): void {
    const animate = () => {
      this.animFrameId = requestAnimationFrame(animate);
      const dt = Math.min(this.clock.getDelta(), 0.1);
      const elapsed = this.clock.elapsedTime;

      this.fpsFrames += 1;
      this.fpsAccum += dt;
      if (this.fpsAccum >= 0.45) {
        this.currentFps = Math.round(this.fpsFrames / this.fpsAccum);
        this.fpsFrames = 0;
        this.fpsAccum = 0;
        this.callbacks.onTelemetryUpdate?.(this.getTelemetry());
      }

      this.temporalCurrent = damp(this.temporalCurrent, this.temporalTarget, 3.2, dt);

      const desireFog = new THREE.Color(0x1b1022);
      const memoryFog = new THREE.Color(0x070d1c);
      this.fogExp2.color.copy(desireFog).lerp(memoryFog, this.temporalCurrent);
      this.renderer.toneMappingExposure = THREE.MathUtils.lerp(1.16, 1.02, this.temporalCurrent);

      if (this.cameraFlightWeight > 0.002 && !this.userDragging) {
        dampVec3(this.camera.position, this.targetCamPos, 3.4, dt);
        dampVec3(this.controls.target, this.targetLookAt, 4.0, dt);
        this.currentFov = damp(this.currentFov, this.targetFov, 3.5, dt);
        if (Math.abs(this.camera.fov - this.currentFov) > 0.02) {
          this.camera.fov = this.currentFov;
          this.camera.updateProjectionMatrix();
        }
        if (
          this.camera.position.distanceTo(this.targetCamPos) < 0.08 &&
          this.controls.target.distanceTo(this.targetLookAt) < 0.06
        ) {
          this.cameraFlightWeight = 0;
        }
      } else if (this.autoOrbit && !this.userDragging) {
        const offset = this.camera.position.clone().sub(this.controls.target);
        const angle = dt * (this.mode === 'specimen' ? 0.35 : 0.065);
        offset.applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);
        this.camera.position.copy(this.controls.target).add(offset);
      }

      this.controls.update();
      this.updateRaycaster();

      if (this.mode === 'city') {
        this.cityInstance.update(elapsed, dt, this.temporalCurrent);
      } else if (this.activeSpecimen) {
        this.cityInstance.temporalUniforms.uTime.value = elapsed;
        this.cityInstance.temporalUniforms.uMemory.value = this.temporalCurrent;
        this.activeSpecimen.update(elapsed, dt, this.temporalCurrent);
      }

      this.gradePass.uniforms.uTime.value = elapsed;
      this.gradePass.uniforms.uMemory.value = this.temporalCurrent;
      this.gradePass.uniforms.uTelescopeFocus.value = damp(
        this.gradePass.uniforms.uTelescopeFocus.value,
        this.cityInstance.temporalUniforms.uTelescopeFocus.value,
        4.0,
        dt
      );

      this.composer.render();
    };

    animate();
  }

  public dispose(): void {
    cancelAnimationFrame(this.animFrameId);
    window.removeEventListener('resize', this.handleResize);
    this.cityInstance.dispose();
    this.activeSpecimen?.dispose();
    this.renderer.dispose();
  }
}
